import { useCallback, useEffect, useRef, useState } from "react";
import { Search, CheckCircle2, X, Plus, Paperclip, Trash2, Loader2, AlertTriangle, Copy, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formTypeLabel, materialStatusColor, materialStatusLabel } from "../lib/formTypes";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader } from "../components/common/PageHeader";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10));
const blankRow = () => ({ rowId: uid(), material_id: "", serial: "", seal_type_id: "", type_label: "", owner: "", auto: true });
// Increment the trailing digits of a serial, preserving zero-padding: TG04858997 -> TG04858998
const nextSerial = (s) => {
  const m = String(s ?? "").match(/^(.*?)(\d+)$/);
  if (!m) return null;
  return m[1] + String(parseInt(m[2], 10) + 1).padStart(m[2].length, "0");
};

// Old Meter quick-pick: searchable dropdown from admin-managed options + an "Other…" free-text fallback.
function OmPick({ label, value, options, onChange, testid }) {
  const inList = options.includes(value);
  const [other, setOther] = useState(!!value && !inList);
  useEffect(() => { setOther(!!value && !options.includes(value)); }, [value, options]);
  const selOptions = [...options.map((o) => ({ value: o, label: o })), { value: "__other__", label: "Other…" }];
  return (
    <div>
      <Label className="text-[11px]">{label} <span className="text-slate-400">(optional)</span></Label>
      <div className="mt-1">
        <SearchableSelect testid={testid} className="h-8" placeholder="Search or select…"
          value={other ? "__other__" : (inList ? value : "")}
          options={selOptions}
          onChange={(v) => { if (v === "__other__") { setOther(true); onChange(""); } else { setOther(false); onChange(v); } }} />
      </div>
      {other && <Input className="mt-1 h-8" placeholder="Enter value" data-testid={`${testid}-other`} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

export default function DataEntry() {
  const { ctx, resumeDraftId, clearResumeDraft, openTab } = useWorkspace();
  const { has } = useAuth();
  const canBulk = has("DATA_ENTRY.UPLOAD");
  const projectId = ctx.project?.id || "";
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formType, setFormType] = useState("");
  const [entry, setEntry] = useState(null);          // {id, entry_number, form_version_id, project_id, ...}
  const entryIdRef = useRef(null);
  const sessRef = useRef(0);
  useEffect(() => { entryIdRef.current = entry?.id || null; if (!entry) sessRef.current += 1; }, [entry]);
  const [render, setRender] = useState(null);
  const [state, setState] = useState(null);
  const [sealTypes, setSealTypes] = useState([]);
  const [omOptions, setOmOptions] = useState({ make: [], status: [], display_status: [] });
  const [geoOpts, setGeoOpts] = useState({ division: [], section: [], subdivision: [] });
  const [finalRemarks, setFinalRemarks] = useState([]);
  const [members, setMembers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [matCache] = useState(() => ({}));           // material_id -> {serial, owner}
  const [busy, setBusy] = useState(false);

  // ---- Bulk Excel upload ----
  const [bulk, setBulk] = useState(null);   // {form, form_type} once a form+type chosen for upload
  const bulkFileRef = useRef();
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkProg, setBulkProg] = useState(null);
  const [bulkErrors, setBulkErrors] = useState(null);
  const [bulkDrafts, setBulkDrafts] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSkipped, setBulkSkipped] = useState(null);
  const [bulkForce, setBulkForce] = useState(false);
  const openBulk = () => setBulk({ form: null, form_type: "" });
  const closeBulk = () => { setBulk(null); setBulkPreview(null); setBulkErrors(null); setBulkDrafts(null); setBulkResult(null); setBulkSkipped(null); setBulkForce(false); };
  const downloadBulkTemplate = async () => {
    try {
      const r = await api.get("/data-entry/bulk/template", { params: { form_definition_id: bulk.form.id, form_type: bulk.form_type }, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = u; a.download = `DataEntry_${bulk.form.form_code || "form"}_${bulk.form_type}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch { toast.error("Could not download template"); }
  };
  const uploadBulk = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBulkBusy(true); setBulkPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("form_definition_id", bulk.form.id); fd.append("form_type", bulk.form_type);
      const { data } = await api.post("/data-entry/bulk/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    finally { setBulkBusy(false); }
  };
  const applyBulk = async () => {
    if (!bulkPreview?.batch_id) return;
    setBulkBusy(true); setBulkProg(null); setBulkErrors(null); setBulkResult(null); setBulkSkipped(null); setBulkDrafts(null);
    const bid = bulkPreview.batch_id;
    const finish = (r) => {
      const failed = r.failed ?? 0;
      const skipped = r.skipped ?? 0;
      const drafted = r.drafted ?? 0;
      const saved = (r.completed ?? 0) + drafted;
      // failures and drafts are different outcomes — never mix them in one list
      setBulkErrors((r.errors || []).slice(0, 20));
      setBulkDrafts((r.draft_reasons || []).slice(0, 50));
      setBulkSkipped((r.skipped_reasons || []).slice(0, 50));
      setBulkResult(r);
      if (failed > 0 && saved === 0) {
        toast.error(`All ${failed} rows failed. ${(r.errors?.[0]?.reason) || ""}`.trim());
      } else {
        toast.success(`Saved to Data Entry Register: ${r.completed ?? 0} completed, ${drafted} draft` + (skipped ? ` — ${skipped} already existed (skipped)` : "") + (failed ? ` — ${failed} failed (fix or retry below)` : "") + ".");
        if (skipped > 0) toast.info("Skipped rows already exist as drafts from your earlier upload. To re-register them fresh from THIS file, tick the replace option in the dialog and click Retry.", { duration: 8000 });
      }
      // keep the dialog open whenever anything needs attention OR drafts were saved, so the outcome summary is visible
      setBulkBusy(false); setBulkProg(null);
      if (failed === 0 && skipped === 0 && drafted === 0) closeBulk();
    };
    try {
      const { data } = await api.post("/data-entry/bulk/apply", { batch_id: bid, force_replace: bulkForce });
      if (data.status === "applied") { finish(data.result || {}); return; }
      // runs in background — poll progress instead of waiting on one long request
      const poll = async (attempts = 0) => {
        if (attempts > 300) { toast.error("Still processing in background — check the Data Entry Register shortly."); setBulkBusy(false); setBulkProg(null); return; }
        try {
          const { data: s } = await api.get(`/data-entry/bulk/apply-status/${bid}`);
          if (s.status === "applied") { finish(s.result || {}); return; }
          if (s.status === "apply_failed") { toast.error(`Apply failed: ${s.error || "unknown error"}`); setBulkBusy(false); setBulkProg(null); return; }
          setBulkProg(s.progress || null);
          setTimeout(() => poll(attempts + 1), 2000);
        } catch { setTimeout(() => poll(attempts + 1), 3000); }
      };
      poll();
    } catch (err) { toast.error(err.response?.data?.detail || "Apply failed"); setBulkBusy(false); setBulkProg(null); }
  };
  const downloadRejected = async () => {
    if (!bulkPreview?.batch_id) return;
    try {
      const r = await api.get(`/data-entry/bulk/rejected/${bulkPreview.batch_id}`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = u; a.download = "DataEntry_Rejected_Rows.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch { toast.error("Could not download rejected rows"); }
  };

  useEffect(() => { api.get("/projects", { params: { page_size: 200 } }).then(() => {}).catch(() => {}); }, []);
  useEffect(() => { api.get("/data-entry/old-meter-options").then((r) => setOmOptions({ make: [], status: [], display_status: [], ...(r.data || {}) })).catch(() => {}); }, []);
  useEffect(() => {
    if (!projectId) { setGeoOpts({ division: [], section: [], subdivision: [] }); return; }
    const pp = { params: { project_id: projectId } };
    Promise.all([api.get("/geo-masters/division", pp), api.get("/geo-masters/section", pp), api.get("/geo-masters/subdivision", pp)]).then(([d, s, sd]) => setGeoOpts({ division: (d.data.items || []).map((x) => x.name), section: (s.data.items || []).map((x) => x.name), subdivision: (sd.data.items || []).map((x) => x.name) })).catch(() => {});
  }, [projectId]);
  useEffect(() => {
    setSelectedForm(null); setFormType(""); setEntry(null); setRender(null); setState(null); setAttachments([]);
    window.__erpUnsaved = false;
    if (!projectId) { setForms([]); return; }
    api.get("/data-entry/forms", { params: { project_id: projectId } }).then((r) => setForms(r.data.items || [])).catch(() => {});
  }, [projectId]);

  useEffect(() => { window.__erpUnsaved = !!entry; return () => { window.__erpUnsaved = false; }; }, [entry]);

  // ---- resume a DRAFT from the register ----
  const loadLookups = async (pid) => Promise.all([
    api.get("/data-entry/final-remarks", { params: { project_id: pid } }).then((x) => x.data.items).catch(() => []),
    api.get("/data-entry/members", { params: { project_id: pid } }).then((x) => x.data.items).catch(() => []),
    api.get("/data-entry/seal-types").then((x) => x.data.items).catch(() => []),
  ]);

  const resumeDraft = useCallback(async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/data-entry/${id}/resume`);
      const [fr, mb, st] = await loadLookups(data.project_id);
      const rd = data.render;
      const d = data.draft || {};
      const materials = {};
      (rd.standard_fields || []).filter((f) => f.is_material).forEach((f) => {
        const fid = f.field_id; const dv = d.materials?.[fid];
        if (f.multi) {
          const rows = (Array.isArray(dv) ? dv : []).map((m) => { matCache[m.material_id] = { serial: m.serial, owner: m.owner, status: m.current_status, make: m.make }; return { rowId: uid(), material_id: m.material_id, serial: m.serial, seal_type_id: m.seal_type_id || "", type_label: m.type_label || "", owner: m.owner, status: m.current_status, make: m.make, ineligible: m.eligible === false, reason: m.reason }; });
          if (f.is_seal) {
            // rebuild the auto-chain: rows whose serial is the next increment of the previous row
            // (from row 1 on, unbroken) stay auto so editing Seal 1 re-cascades; anything else is manual
            let alive = true; let prevS = rows[0]?.serial || null;
            rows.forEach((r, j) => {
              if (j === 0) { r.auto = false; return; }
              r.auto = alive && !!prevS && !!r.serial && String(r.serial) === nextSerial(prevS);
              if (!r.auto) alive = false;
              if (r.serial) prevS = r.serial;
            });
          }
          materials[fid] = rows.length ? rows : [blankRow()];
        } else if (dv && dv.material_id) {
          matCache[dv.material_id] = { serial: dv.serial, owner: dv.owner, status: dv.current_status, make: dv.make };
          materials[fid] = { material_id: dv.material_id, serial: dv.serial, owner: dv.owner, status: dv.current_status, make: dv.make, ineligible: dv.eligible === false, reason: dv.reason };
        }
      });
      setSelectedForm({ id: data.form_definition_id, form_name: rd.form_name, form_code: "", current_version_no: data.form_version_no, form_types: (data.form_types && data.form_types.length ? data.form_types : [data.form_type]) });
      setFormType(data.form_type);
      sessRef.current += 1;
      setEntry({ id: data.id, entry_number: data.entry_number, form_version_id: data.form_version_id, form_type: data.form_type, project_id: data.project_id, source: data.source });
      setRender(rd); setFinalRemarks(fr); setMembers(mb); setSealTypes(st);
      setState({ values: d.values || {}, consumer: d.consumer || null, consumerQuery: "", consumerResults: [], materials, membersSel: d.members || {}, final_remark_id: d.final_remark_id || "", old_meter: d.old_meter || {} });
      const att = await api.get(`/data-entry/${id}/attachments`).then((x) => x.data.items).catch(() => []);
      setAttachments(att || []);
      const allMats = []; Object.values(materials).forEach((v) => Array.isArray(v) ? allMats.push(...v) : allMats.push(v));
      const bad = allMats.filter((m) => m && m.ineligible).length;
      toast.success(`Draft ${data.entry_number} reopened${bad ? ` · ${bad} material(s) need review` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resume draft"); }
    finally { setBusy(false); }
  }, [matCache]);

  useEffect(() => {
    if (!resumeDraftId) return;
    const id = resumeDraftId;
    clearResumeDraft();
    resumeDraft(id);
  }, [resumeDraftId, resumeDraft, clearResumeDraft]);

  const openForm = (f) => { setSelectedForm(f); setFormType(""); setEntry(null); setRender(null); setState(null); setAttachments([]); };
  const backToForms = () => { setSelectedForm(null); setFormType(""); setEntry(null); setRender(null); setState(null); setAttachments([]); };

  const startEntry = async (ft) => {
    if (!selectedForm || !ft) return;
    setFormType(ft);
    setBusy(true);
    try {
      const r = await api.get(`/data-entry/forms/${selectedForm.id}/render`, { params: { form_type: ft } });
      const [fr, mb, st] = await loadLookups(projectId);
      const materials = {};
      (r.data.standard_fields || []).filter((f) => f.is_material && f.multi).forEach((f) => { materials[f.field_id] = f.is_seal ? [blankRow(), blankRow(), blankRow()] : [blankRow()]; });
      // Do NOT persist a draft yet — the entry is created lazily on first Submit / attachment.
      sessRef.current += 1;
      setEntry({ id: null, entry_number: null, project_id: projectId, form_definition_id: selectedForm.id, form_type: ft }); setRender(r.data); setFinalRemarks(fr); setMembers(mb); setSealTypes(st);
      setAttachments([]);
      setState({ values: {}, consumer: null, consumerQuery: "", consumerResults: [], materials, membersSel: {}, final_remark_id: "", old_meter: {} });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start entry"); setFormType(""); }
    finally { setBusy(false); }
  };

  const switchType = async (ft) => {
    if (!ft || ft === formType || !selectedForm) return;
    setBusy(true);
    try {
      const r = await api.get(`/data-entry/forms/${selectedForm.id}/render`, { params: { form_type: ft } });
      const nr = r.data;
      // Keep everything already entered; only adapt the shape to the new type's schema.
      setState((s) => {
        const materials = {};
        (nr.standard_fields || []).filter((f) => f.is_material).forEach((f) => {
          const prev = s.materials?.[f.field_id];
          if (f.multi) materials[f.field_id] = (Array.isArray(prev) && prev.length) ? prev : (f.is_seal ? [blankRow(), blankRow(), blankRow()] : [blankRow()]);
          else if (prev?.material_id) materials[f.field_id] = prev;
        });
        const cfIds = new Set((nr.custom_fields || []).map((c) => c.field_id));
        const values = {};
        Object.entries(s.values || {}).forEach(([k, v]) => { if (k === "INSTALLATION_DATE" || cfIds.has(k)) values[k] = v; });
        const membersSel = {};
        Object.keys(nr.members_config || {}).forEach((role) => { if ((s.membersSel?.[role] || []).length) membersSel[role] = s.membersSel[role]; });
        return { ...s, values, materials, membersSel };
      });
      setFormType(ft);
      setRender(nr);
      setEntry((e) => ({ ...e, form_type: ft }));
      toast.info(`Type changed to ${formTypeLabel(ft)} — entered details kept`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to switch type"); }
    finally { setBusy(false); }
  };
  // Lazily create the draft only when the user actually saves/attaches — visiting/cancelling leaves nothing behind.
  const ensureEntry = async (withNumber = false) => {
    if (entry?.id) return withNumber ? { id: entry.id, number: entry.entry_number } : entry.id;
    const mySess = sessRef.current;
    const { data } = await api.post("/data-entry", { project_id: projectId, form_definition_id: selectedForm.id, form_type: formType });
    // only adopt the new draft if the user is still on the same (unsaved) entry
    if (sessRef.current === mySess) setEntry((e) => ({ ...(e || {}), id: data.id, entry_number: data.entry_number, form_version_id: data.form_version_id }));
    return withNumber ? { id: data.id, number: data.entry_number } : data.id;
  };

  const enabled = useCallback((fid) => (render?.standard_fields || []).some((f) => f.field_id === fid), [render]);
  const stdField = useCallback((fid) => (render?.standard_fields || []).find((f) => f.field_id === fid), [render]);

  // ---- consumer autocomplete (debounced, latest-wins, cancellable) ----
  const consumerDebRef = useRef();
  const consumerSeqRef = useRef(0);
  const consumerAbortRef = useRef(null);
  const searchConsumer = (q) => {
    setState((s) => ({ ...s, consumerQuery: q }));
    clearTimeout(consumerDebRef.current);
    if (!q || q.length < 2) { setState((s) => ({ ...s, consumerResults: [], consumerLoading: false })); return; }
    setState((s) => ({ ...s, consumerLoading: true }));
    consumerDebRef.current = setTimeout(async () => {
      if (consumerAbortRef.current) consumerAbortRef.current.abort();
      const controller = new AbortController();
      consumerAbortRef.current = controller;
      const reqId = ++consumerSeqRef.current;
      try {
        const { data } = await api.get("/data-entry/consumers/search", { params: { q }, signal: controller.signal });
        if (reqId !== consumerSeqRef.current) return;   // stale response — newer keystroke won
        setState((s) => ({ ...s, consumerResults: data.items || [], consumerLoading: false }));
      } catch {
        if (reqId === consumerSeqRef.current) setState((s) => ({ ...s, consumerResults: [], consumerLoading: false }));
      }
    }, 250);
  };
  const pickConsumer = (c) => setState((s) => ({ ...s, consumer: { consumer_id: c.id, consumer_number: c.consumer_number, name: c.name, address: c.address, mobile: c.mobile, area: c.area, discom: c.discom, division: c.division, section: c.section, old_meter_no: c.old_meter_no, meter_kw: c.meter_kw }, consumerResults: [], consumerQuery: "" }));

  // ---- inline material search (server-side, eligibility enforced by backend) ----
  const materialFields = (render?.standard_fields || []).filter((f) => f.is_material);
  const selectedMaterialIds = (exceptRole, exceptRowId) => {
    const ids = [];
    materialFields.forEach((f) => {
      const val = state?.materials[f.field_id];
      if (f.multi) { (val || []).forEach((x) => { if (x.rowId !== exceptRowId && x.material_id) ids.push(x.material_id); }); }
      else if (f.field_id !== exceptRole && val?.material_id) ids.push(val.material_id);
    });
    return ids;
  };
  const searchMaterials = async (q, excludeIds, signal, fieldId) => {
    const pid = entry?.project_id || projectId;
    if (!pid) return [];
    const { data } = await api.get("/data-entry/materials/search", { params: { project_id: pid, q, field_id: fieldId || "" }, signal });
    const blocked = (data.blocked || []).map((b, i) => ({
      value: `__blocked_${i}`, label: b.serial, disabled: true,
      sub: <span className="font-semibold text-amber-700">Not selectable: {b.reason}</span>,
    }));
    return (data.items || [])
      .filter((m) => !excludeIds.includes(m.id))
      .map((m) => {
        matCache[m.id] = { serial: m.material_number, serial_norm: m.serial_normalized, owner: m.assigned_field_user_name, status: m.current_status, make: m.make };
        return {
          value: m.id, label: m.material_number,
          sub: (
            <span className="flex flex-wrap items-center gap-1">
              <span className="text-slate-500">{m.type_name}</span>
              {m.make && m.make !== "—" && (<><span className="text-slate-300">·</span><span className="text-slate-600">make: {m.make}</span></>)}
              <span className="text-slate-300">·</span>
              <span className={`font-bold ${materialStatusColor(m.current_status)}`}>{materialStatusLabel(m.current_status)}</span>
              <span className="text-slate-300">·</span>
              <span className="font-bold text-slate-700">{m.assigned_field_user_name}</span>
            </span>
          ),
        };
      })
      .concat(blocked);
  };

  const selectSingle = (role, v) => setState((s) => {
    if (!v) { const cp = { ...s.materials }; delete cp[role]; return { ...s, materials: cp }; }
    const c = matCache[v] || {};
    return { ...s, materials: { ...s.materials, [role]: { material_id: v, serial: c.serial, owner: c.owner, status: c.status, make: c.make } } };
  });

  const setRow = (fid, rowId, patch) => setState((s) => ({ ...s, materials: { ...s.materials, [fid]: (s.materials[fid] || []).map((x) => x.rowId === rowId ? { ...x, ...patch } : x) } }));
  const stateRef = useRef(null); stateRef.current = state;
  const cascadeSeq = useRef({});

  // Seal auto-increment: Seal 1 picked -> the following still-auto rows get the next serials
  // (TG04858997 -> TG04858998, TG04858999, ...). A manual edit in row N freezes the chain from
  // N onward, so a hand-picked Seal 2 never re-derives Seal 3. Auto-filled only when the next
  // serial exists in the material register; otherwise the row is left blank for manual selection.
  const bizSerial = (r) => r?.serial_norm || (r?.serial != null ? String(r.serial) : "");
  const cascadeSeals = async (fid) => {
    const my = (cascadeSeq.current[fid] = (cascadeSeq.current[fid] || 0) + 1);
    const pid = entry?.project_id || projectId;
    if (!pid) return;
    let rows = (stateRef.current?.materials?.[fid]) || [];
    if (rows.length < 2) return;
    let prevSerial = bizSerial(rows[0]);
    for (let j = 1; j < rows.length; j++) {
      if (cascadeSeq.current[fid] !== my) return;
      rows = (stateRef.current?.materials?.[fid]) || [];
      const row = rows[j]; const prev = rows[j - 1];
      if (!row) return;
      if (prev && bizSerial(prev)) prevSerial = bizSerial(prev);
      if (!row.auto) break;
      const expected = prevSerial ? nextSerial(prevSerial) : null;
      const clearRow = () => setRow(fid, row.rowId, { material_id: "", serial: "", serial_norm: "", owner: "", status: null, ineligible: false, reason: null, auto: true });
      if (!expected) { if (row.material_id) clearRow(); continue; }
      if (row.material_id && bizSerial(row).toUpperCase() === expected.toUpperCase()) { prevSerial = bizSerial(row); continue; }
      const usedIds = [];
      Object.values(stateRef.current?.materials || {}).forEach((v) => {
        if (Array.isArray(v)) v.forEach((x) => { if (x.rowId !== row.rowId && x.material_id) usedIds.push(x.material_id); });
        else if (v?.material_id) usedIds.push(v.material_id);
      });
      let found = null;
      try {
        const { data } = await api.get("/data-entry/materials/search", { params: { project_id: pid, q: expected, field_id: fid } });
        found = (data.items || []).find((m) => ((m.serial_normalized || m.material_number || "").toUpperCase() === expected.toUpperCase()) && !usedIds.includes(m.id));
        if (found) matCache[found.id] = { serial: found.material_number, serial_norm: found.serial_normalized, owner: found.assigned_field_user_name, status: found.current_status, make: found.make };
      } catch { /* network hiccup — leave the row for manual selection */ }
      if (cascadeSeq.current[fid] !== my) return;
      if (found) setRow(fid, row.rowId, { material_id: found.id, serial: found.material_number, serial_norm: found.serial_normalized, owner: found.assigned_field_user_name, status: found.current_status, make: found.make, ineligible: false, reason: null, auto: true });
      else clearRow();
      prevSerial = expected;
    }
  };

  const selectRowMaterial = (fid, rowId, v) => {
    const fld = (materialFields || []).find((f) => f.field_id === fid);
    const isSeal = !!fld?.is_seal;
    if (!v) { setRow(fid, rowId, { material_id: "", serial: "", serial_norm: "", owner: "", status: null, ineligible: false, reason: null, auto: false }); }
    else {
      const c = matCache[v] || {};
      setRow(fid, rowId, { material_id: v, serial: c.serial, serial_norm: c.serial_norm, owner: c.owner, status: c.status, make: c.make, ineligible: false, reason: null, auto: false });
    }
    if (!isSeal) return;
    const idx = (stateRef.current?.materials?.[fid] || []).findIndex((x) => x.rowId === rowId);
    if (idx === 0) {
      setTimeout(() => cascadeSeals(fid), 0);
    } else if (idx > 0) {
      setState((s) => ({ ...s, materials: { ...s.materials, [fid]: (s.materials[fid] || []).map((x, j) => (j >= idx ? { ...x, auto: false } : x)) } }));
    }
  };
  const addRow = (fid) => {
    setState((s) => ({ ...s, materials: { ...s.materials, [fid]: [...(s.materials[fid] || []), blankRow()] } }));
    const fld = (materialFields || []).find((f) => f.field_id === fid);
    if (fld?.is_seal) setTimeout(() => cascadeSeals(fid), 0);
  };
  const removeRow = (fid, rowId) => setState((s) => {
    const next = (s.materials[fid] || []).filter((x) => x.rowId !== rowId);
    return { ...s, materials: { ...s.materials, [fid]: next.length ? next : [blankRow()] } };
  });

  // ---- attachments ----
  const CHUNK_BYTES = 900 * 1024; // stay under proxy body limits on large files
  const uploadChunked = async (id, file) => {
    const { data: init } = await api.post(`/data-entry/${id}/attachments/chunked/init`);
    const parts = Math.ceil(file.size / CHUNK_BYTES);
    for (let i = 0; i < parts; i++) {
      toast.info(`Uploading ${file.name}: part ${i + 1}/${parts}`, { id: `att-${file.name}` });
      await api.put(`/data-entry/${id}/attachments/chunked/chunk`, file.slice(i * CHUNK_BYTES, (i + 1) * CHUNK_BYTES), {
        params: { upload_id: init.upload_id, index: i },
        headers: { "Content-Type": "application/octet-stream" },
      });
    }
    toast.info(`Processing ${file.name}…`, { id: `att-${file.name}` });
    await api.post(`/data-entry/${id}/attachments/chunked/finish`, null, { params: { upload_id: init.upload_id, filename: file.name } });
    toast.dismiss(`att-${file.name}`);
  };
  const refreshAttachments = useCallback(async (id) => {
    const { data } = await api.get(`/data-entry/${id}/attachments`);
    if (entryIdRef.current === id) setAttachments(data.items || []);
    return data.items || [];
  }, []);
  const hasProcessing = attachments.some((a) => a.status === "processing");
  useEffect(() => {
    if (!hasProcessing || !entry?.id) return undefined;
    const t = setInterval(() => { refreshAttachments(entry.id).catch(() => {}); }, 3000);
    return () => clearInterval(t);
  }, [hasProcessing, entry?.id, refreshAttachments]);
  // Uploads keep running if the user saves and moves to a new entry; compression runs on the server.
  const uploadFiles = async (fileList) => {
    if (!entry) return;
    const files = Array.from(fileList);
    const mySess = sessRef.current;
    let id, label;
    try { ({ id, number: label } = await ensureEntry(true)); } catch (e) { toast.error(e.response?.data?.detail || "Could not create the entry"); return; }
    label = label || "the draft";
    const pending = files.map((f, i) => ({ id: `__up_${Date.now()}_${i}`, original_filename: f.name, status: "uploading" }));
    if (sessRef.current === mySess) setAttachments((a) => [...a, ...pending]);
    try {
      const small = files.filter((f) => f.size <= CHUNK_BYTES);
      const big = files.filter((f) => f.size > CHUNK_BYTES);
      if (small.length) {
        const fd = new FormData();
        small.forEach((f) => fd.append("files", f));
        await api.post(`/data-entry/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      for (const f of big) await uploadChunked(id, f);
      toast.success(entryIdRef.current === id ? "Uploaded — compressing in the background. You can save now." : `Attachment uploaded for ${label}`);
    } catch (e) {
      toast.error(`${label}: ` + (e.response?.data?.detail || `Upload failed (HTTP ${e.response?.status || "network error"})`));
    }
    refreshAttachments(id).catch(() => {});
  };
  const deleteAttachment = async (aid) => {
    try { await api.delete(`/data-entry/attachments/${aid}`); setAttachments((a) => a.filter((x) => x.id !== aid)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const viewAttachment = async (a) => {
    try {
      const r = await api.get(`/data-entry/attachments/${a.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data], { type: a.final_mime || r.data.type }));
      window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not open attachment"); }
  };

  // ---- payload builder ----
  const buildMaterials = () => {
    const out = {};
    materialFields.forEach((f) => {
      const val = state.materials[f.field_id];
      if (f.multi) {
        const rows = (val || []).filter((x) => x.material_id).map((x) => f.is_seal
          ? { material_id: x.material_id, serial: x.serial, seal_type_id: x.seal_type_id }
          : { material_id: x.material_id, serial: x.serial, type_label: x.type_label || "" });
        if (rows.length) out[f.field_id] = rows;
      } else if (val?.material_id) {
        out[f.field_id] = { material_id: val.material_id, serial: val.serial };
      }
    });
    return out;
  };
  const buildPayload = () => ({
    form_type: formType,
    values: state.values,
    consumer: enabled("CONSUMER") ? state.consumer : null,
    materials: buildMaterials(),
    members: enabled("MEMBERS") ? state.membersSel : {},
    final_remark_id: enabled("FINAL_REMARK") ? (state.final_remark_id || null) : null,
    old_meter: state.old_meter,
    attachment_ids: attachments.filter((a) => a.status === "attached" || a.status === "processing").map((a) => a.id),
  });

  const resetForNewEntry = () => { setEntry(null); setRender(null); setState(null); setAttachments([]); setFormType(""); };

  const submit = async () => {
    if (!entry) return;
    setBusy(true);
    try {
      const id = await ensureEntry();
      const payload = buildPayload();
      // 1) persist everything as a draft first (also refreshes the material reservation lock)
      await api.put(`/data-entry/${id}`, payload);
      // 2) try to complete — succeeds only when every material is Acknowledged/Installed
      try {
        const { data } = await api.post(`/data-entry/${id}/complete`, { ...payload, idempotency_key: crypto.randomUUID() });
        toast.success(`Completed · ${data.entry_number} · ${data.materials_consumed} material(s) consumed`);
        resetForNewEntry();
      } catch (ce) {
        if (ce.response?.status === 409) {
          // Materials not yet Acknowledged/Installed → correctly kept as a draft.
          toast.info("Saved as draft", { description: "It will complete once all materials are Acknowledged/Installed." });
          resetForNewEntry();
        } else {
          // Validation issue (missing/invalid field) → keep the user on the form to fix it.
          toast.error(ce.response?.data?.detail || "Please complete the required fields to submit");
        }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const showOldMeter = render && ["OLD_REPLACED", "REWORK"].includes(render.form_type);

  return (
    <div>
      <PageHeader title="Data Entry" subtitle="Pick a project form and start capturing entries"
        actions={ctx.project
          ? <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm" data-testid="de-active-project"><span className="text-xs text-slate-500">Active Project:</span> <span className="font-semibold text-blue-800">{ctx.project.project_code ? `${ctx.project.project_code} — ` : ""}{ctx.project.project_name}</span></div>
          : <span className="text-sm text-amber-600" data-testid="de-no-project">Select an Active Project from the top bar</span>} />

      {/* STEP 1 — available published forms as cards */}
      {!selectedForm && (
        <div data-testid="de-forms-list">
          {!projectId && <p className="text-sm text-slate-500">Select an Active Project (top bar) to see its published forms.</p>}
          {projectId && forms.length === 0 && <p className="text-sm text-amber-600">No active published forms for this project. Publish a form in Form Builder first.</p>}
          {projectId && forms.length > 0 && canBulk && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-4 py-3">
              <div className="text-sm text-teal-800"><span className="font-semibold">Bulk Upload</span> — create many entries at once from an Excel template (Draft/Completed decided by the same rules).</div>
              <Button size="sm" className="bg-teal-700 hover:bg-teal-800" data-testid="de-bulk-open" onClick={openBulk}><Plus className="mr-1 h-4 w-4" />Bulk Upload</Button>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {forms.map((f) => (
              <div key={f.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-400 hover:shadow-md" data-testid={`de-form-card-${f.id}`}>
                <div className="mb-1 font-display text-base font-semibold text-slate-800">{f.form_name}</div>
                <div className="font-mono text-xs text-slate-500">{f.form_code}</div>
                <div className="mt-1 text-xs text-slate-400">Version v{f.current_version_no} · {(f.form_types || []).length} form type(s)</div>
                <Button className="mt-3 w-full bg-blue-600 hover:bg-blue-700" data-testid={`de-open-form-${f.id}`} onClick={() => openForm(f)}>Open Form</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2 — form opened, choose Form Type INSIDE the form */}
      {selectedForm && !entry && (
        <div className="max-w-xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="de-formtype-step">
          <button className="mb-3 text-xs text-blue-600 underline" data-testid="de-back-forms" onClick={backToForms}>← Back to forms</button>
          <div className="font-display text-lg font-semibold text-slate-800">{selectedForm.form_name}</div>
          <div className="font-mono text-xs text-slate-500">{selectedForm.form_code} · v{selectedForm.current_version_no}</div>
          <div className="mt-4">
            <Label className="text-xs">Form Type *</Label>
            <Select value={formType} onValueChange={startEntry} disabled={busy}>
              <SelectTrigger className="mt-1" data-testid="de-formtype-select"><SelectValue placeholder={busy ? "Starting…" : "Select form type to begin"} /></SelectTrigger>
              <SelectContent>{(selectedForm.form_types || []).map((t) => <SelectItem key={t} value={t}>{formTypeLabel(t)}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-2 text-xs text-slate-400">Fields adapt to the selected form type.</p>
          </div>
        </div>
      )}

      {entry && render && state && (
        <div className="max-w-4xl space-y-5" data-testid="data-entry-form">
          <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <div><div className="font-mono text-sm font-semibold text-blue-800" data-testid="de-entry-number">{entry.entry_number}</div>
              <div className="text-xs text-slate-500">{render.form_name} · {formTypeLabel(render.form_type)} · v{render.version_no} <Badge variant="outline" className="ml-1">DRAFT</Badge></div></div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Type:</span>
              <Select value={formType} onValueChange={switchType} disabled={busy}>
                <SelectTrigger className="h-8 w-48" data-testid="de-change-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(selectedForm.form_types || []).map((t) => <SelectItem key={t} value={t} data-testid={`de-change-type-${t}`}>{formTypeLabel(t)}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={backToForms} data-testid="de-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Installation Date */}
          {enabled("INSTALLATION_DATE") && (
            <Section title="Installation Date" required={stdField("INSTALLATION_DATE")?.required}>
              <Input type="date" data-testid="de-installation-date" className="max-w-xs" value={state.values.INSTALLATION_DATE || ""}
                onChange={(e) => setState((s) => ({ ...s, values: { ...s.values, INSTALLATION_DATE: e.target.value } }))} />
            </Section>
          )}

          {/* Consumer */}
          {enabled("CONSUMER") && (
            <Section title="Consumer" required={stdField("CONSUMER")?.required}>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="de-consumer-search" className="pl-9" placeholder="Search consumer number / name / mobile to auto-fill" value={state.consumerQuery} onChange={(e) => searchConsumer(e.target.value)} />
                {state.consumerResults.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-white shadow-lg">
                    {state.consumerResults.map((c) => (
                      <button key={c.id} data-testid={`de-consumer-opt-${c.id}`} className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => pickConsumer(c)}>
                        <span className="font-mono font-medium">{c.consumer_number}</span><span className="text-xs text-slate-500">{c.name} {c.mobile ? `· ${c.mobile}` : ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Manual Override — fill details directly</span>
                  {state.consumer && <button className="text-xs text-red-600 underline" data-testid="de-consumer-clear" onClick={() => setState((s) => ({ ...s, consumer: null, consumerQuery: "" }))}>clear</button>}
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {["consumer_number", "name", "mobile", "address", "area", "division", "section", "meter_kw", "old_meter_no"].map((k) => {
                    const cur = (state.consumer || {})[k] || "";
                    const geoKey = k === "area" ? "subdivision" : ((k === "division" || k === "section") ? k : null);
                    if (geoKey) {
                      const opts = geoOpts[geoKey] || [];
                      const selOptions = opts.map((o) => ({ value: o, label: o }));
                      if (cur && !opts.includes(cur)) selOptions.unshift({ value: cur, label: cur });
                      const lbl = k === "area" ? "Subdivision" : k;
                      return (
                        <div key={k}><Label className="text-[11px] capitalize">{lbl}</Label>
                          <div className="mt-1">
                            <SearchableSelect testid={`de-consumer-${k}`} className="h-8" placeholder={`Search ${lbl.toLowerCase()}…`}
                              value={cur} options={selOptions}
                              onChange={(v) => setState((s) => ({ ...s, consumer: { ...(s.consumer || {}), [k]: v } }))} />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={k}><Label className="text-[11px] capitalize">{k === "meter_kw" ? "Meter KW" : k.replace(/_/g, " ")}</Label>
                        <Input data-testid={`de-consumer-${k}`} className="mt-1 h-8" value={cur} onChange={(e) => setState((s) => ({ ...s, consumer: { ...(s.consumer || {}), [k]: e.target.value } }))} /></div>
                    );
                  })}
                </div>
              </div>
            </Section>
          )}

          {/* Materials — generic (single picker; multi rows for Seal/marked-multi types) */}
          {materialFields.map((f) => {
            if (!f.multi) {
              const sel = state.materials[f.field_id];
              return (
                <Section key={f.field_id} title={f.label} required={f.required}>
                  <div className="max-w-md" data-testid={`de-mat-${f.field_id}`}>
                    <SearchableSelect testid={`de-mat-select-${f.field_id}`} value={sel?.material_id || ""}
                      onChange={(v) => selectSingle(f.field_id, v)}
                      loadOptions={(q, signal) => searchMaterials(q, selectedMaterialIds(f.field_id, null), signal, f.field_id)}
                      options={sel?.material_id ? [{ value: sel.material_id, label: sel.serial }] : []}
                      error={sel?.ineligible} placeholder={`Search ${f.label} serial…`} />
                    {sel?.status && <div className="mt-1 text-xs" data-testid={`de-mat-status-${f.field_id}`}>status: <span className={`font-bold ${materialStatusColor(sel.status)}`}>{materialStatusLabel(sel.status)}</span>{sel.make && sel.make !== "—" && <span className="text-slate-600"> · make: <span className="font-semibold" data-testid={`de-mat-make-${f.field_id}`}>{sel.make}</span></span>}</div>}
                    {sel?.owner && <div className="mt-1 text-xs text-slate-500">owner: {sel.owner}</div>}
                    {sel?.ineligible && <div className="mt-1 flex items-center gap-1 text-xs text-red-600" data-testid={`de-mat-warn-${f.field_id}`}><AlertTriangle className="h-3.5 w-3.5" /> {sel.reason || "No longer eligible — reselect"}</div>}
                  </div>
                </Section>
              );
            }
            const rows = state.materials[f.field_id] || [];
            const unit = f.is_seal ? "Seal" : f.label;
            return (
              <Section key={f.field_id} title={f.is_seal ? "Seals" : f.label} required={f.required}>
                <div className="space-y-2" data-testid={`de-multi-${f.field_id}`}>
                  {rows.map((row, i) => (
                    <div key={row.rowId} className="rounded-lg border p-3" data-testid={`de-row-${f.field_id}-${i}`}>
                      <div className="flex flex-wrap items-start gap-3">
                        <span className="mt-2 w-14 shrink-0 text-xs text-slate-400">{unit} {i + 1}</span>
                        <div className="min-w-[200px] flex-1">
                          <SearchableSelect testid={`de-row-serial-${f.field_id}-${i}`} value={row.material_id || ""}
                            onChange={(v) => selectRowMaterial(f.field_id, row.rowId, v)}
                            loadOptions={(q, signal) => searchMaterials(q, selectedMaterialIds(null, row.rowId), signal, f.field_id)}
                            options={row.material_id ? [{ value: row.material_id, label: row.serial }] : []}
                            error={row.ineligible} placeholder={`Search ${f.label} serial…`} />
                          {f.is_seal && row.serial && (
                            <div data-testid={`de-row-copy-seal-${f.field_id}-${i}`} role="button" tabIndex={0}
                              title="Seal number — click to copy, or drag-select to copy"
                              onClick={async () => {
                                try { await navigator.clipboard.writeText(row.serial); toast.success(`Copied seal no: ${row.serial}`); return; } catch { /* fall through to selection-based copy */ }
                                try { if (document.execCommand("copy")) { toast.success(`Copied seal no: ${row.serial}`); return; } } catch { /* ignore */ }
                                toast.error("Copy failed — drag-select the number instead");
                              }}
                              className="mt-1 inline-flex cursor-copy items-center gap-1.5 rounded-md border border-dashed border-teal-300 bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800 hover:bg-teal-100">
                              <Copy className="h-3 w-3 shrink-0" /><span className="select-all">{row.serial}</span>
                            </div>
                          )}
                          {row.status && <div className="mt-1 text-xs" data-testid={`de-row-status-${f.field_id}-${i}`}>status: <span className={`font-bold ${materialStatusColor(row.status)}`}>{materialStatusLabel(row.status)}</span>{row.make && row.make !== "—" && <span className="text-slate-600"> · make: <span className="font-semibold">{row.make}</span></span>}</div>}
                          {row.owner && <div className="mt-1 text-xs text-slate-500">owner: {row.owner}</div>}
                          {row.ineligible && <div className="mt-1 flex items-center gap-1 text-xs text-red-600" data-testid={`de-row-warn-${f.field_id}-${i}`}><AlertTriangle className="h-3.5 w-3.5" /> {row.reason || "No longer eligible — reselect"}</div>}
                        </div>
                        <div className="w-56">
                          {f.is_seal
                            ? <SearchableSelect testid={`de-seal-type-${i}`} value={row.seal_type_id}
                                onChange={(v) => setRow(f.field_id, row.rowId, { seal_type_id: v })}
                                options={sealTypes.map((t) => ({ value: t.id, label: t.name }))} placeholder="Seal type *" />
                            : <Input data-testid={`de-row-type-${f.field_id}-${i}`} value={row.type_label || ""} placeholder="Type (optional)"
                                onChange={(e) => setRow(f.field_id, row.rowId, { type_label: e.target.value })} />}
                        </div>
                        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => removeRow(f.field_id, row.rowId)} data-testid={`de-row-remove-${f.field_id}-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="border-dashed" onClick={() => addRow(f.field_id)} data-testid={`de-row-add-${f.field_id}`}><Plus className="mr-1 h-4 w-4" /> Add More {unit}</Button>
                </div>
              </Section>
            );
          })}

          {/* Old meter (replaced/rework) */}
          {showOldMeter && (
            <Section title="Old Meter">
              <div className="grid gap-3 sm:grid-cols-2 max-w-lg">
                <div><Label className="text-[11px]">Old Meter No</Label><Input data-testid="de-old-meter-no" className="mt-1 h-8" value={state.old_meter.old_meter_no || ""} onChange={(e) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, old_meter_no: e.target.value } }))} /></div>
                <div><Label className="text-[11px]">Final Reading</Label><Input data-testid="de-old-meter-reading" className="mt-1 h-8" value={state.old_meter.final_reading || ""} onChange={(e) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, final_reading: e.target.value } }))} /></div>
                <OmPick label="Old Meter Make" testid="de-old-meter-make" options={omOptions.make} value={state.old_meter.old_meter_make || ""} onChange={(v) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, old_meter_make: v } }))} />
                <OmPick label="Old Meter Status" testid="de-old-meter-status" options={omOptions.status} value={state.old_meter.old_meter_status || ""} onChange={(v) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, old_meter_status: v } }))} />
                <OmPick label="Display Status" testid="de-old-meter-display-status" options={omOptions.display_status} value={state.old_meter.display_status || ""} onChange={(v) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, display_status: v } }))} />
                <div><Label className="text-[11px]">Old Meter Collected or Not <span className="text-slate-400">(optional)</span></Label>
                  <Select value={state.old_meter.collected || ""} onValueChange={(v) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, collected: v } }))}>
                    <SelectTrigger data-testid="de-old-meter-collected" className="mt-1 h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Collected" data-testid="de-old-meter-collected-yes">Collected</SelectItem>
                      <SelectItem value="Not collected" data-testid="de-old-meter-collected-no">Not collected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[11px]">Other <span className="text-slate-400">(optional)</span></Label><Input data-testid="de-old-meter-other" className="mt-1 h-8" value={state.old_meter.other || ""} onChange={(e) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, other: e.target.value } }))} /></div>
                <div><Label className="text-[11px]">Old Meter Manufacturing Date <span className="text-slate-400">(optional)</span></Label><Input type="date" data-testid="de-old-meter-mfg-date" className="mt-1 h-8" value={state.old_meter.mfg_date || ""} onChange={(e) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, mfg_date: e.target.value } }))} /></div>
                <div><Label className="text-[11px]">GP/Non GP <span className="text-slate-400">(optional)</span></Label>
                  <Select value={state.old_meter.gp || ""} onValueChange={(v) => setState((s) => ({ ...s, old_meter: { ...s.old_meter, gp: v } }))}>
                    <SelectTrigger data-testid="de-old-meter-gp" className="mt-1 h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes" data-testid="de-old-meter-gp-yes">Yes</SelectItem>
                      <SelectItem value="No" data-testid="de-old-meter-gp-no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Section>
          )}

          {/* Final Remark */}
          {enabled("FINAL_REMARK") && (
            <Section title="Final Remark" required={stdField("FINAL_REMARK")?.required}>
              <div className="max-w-md"><SearchableSelect testid="de-final-remark" value={state.final_remark_id}
                onChange={(v) => setState((s) => ({ ...s, final_remark_id: v }))}
                options={finalRemarks.map((f) => ({ value: f.id, label: f.text, sub: f.code }))} placeholder="Select final remark" /></div>
            </Section>
          )}

          {/* Members — searchable from Member Master (category-independent: any member fits any role) */}
          {enabled("MEMBERS") && (
            <Section title="Members">
              <div className="space-y-3">
                {Object.entries(render.members_config || {}).map(([role, cfg]) => {
                  const memberOptions = members.map((m) => ({ value: m.id, label: m.member_name, sub: `${m.member_code}${m.category_name && m.category_name !== "—" ? " · " + m.category_name : ""}` }));
                  const sel = state.membersSel[role] || [];
                  return (
                    <div key={role}>
                      <Label className="text-[11px]">{role.replace(/_/g, " ")} {cfg.required && <span className="text-red-500">*</span>} {cfg.multi && <span className="text-slate-400">(multi)</span>}</Label>
                      <div className="mt-1 max-w-xl">
                        {cfg.multi ? (
                          <SearchableSelect testid={`de-member-${role}`} multiple value={sel} options={memberOptions}
                            placeholder={`Search ${role.replace(/_/g, " ").toLowerCase()}...`}
                            onChange={(arr) => setState((s) => ({ ...s, membersSel: { ...s.membersSel, [role]: arr } }))} />
                        ) : (
                          <SearchableSelect testid={`de-member-${role}`} value={sel[0] || ""} options={memberOptions}
                            placeholder={`Search ${role.replace(/_/g, " ").toLowerCase()}...`}
                            onChange={(v) => setState((s) => ({ ...s, membersSel: { ...s.membersSel, [role]: v ? [v] : [] } }))} />
                        )}
                        {members.length === 0 && <span className="text-xs text-slate-400">No members assigned to this project</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Custom fields */}
          {(render.custom_fields || []).length > 0 && (
            <Section title="Additional Details">
              <div className="grid gap-4 sm:grid-cols-2">
                {render.custom_fields.map((cf) => <CustomField key={cf.field_id} cf={cf} value={state.values[cf.field_id]} onChange={(v) => setState((s) => ({ ...s, values: { ...s.values, [cf.field_id]: v } }))} />)}
              </div>
            </Section>
          )}

          {/* Attachments */}
          {render.attachments?.enabled && (
            <Section title="Attachments" required={render.attachments?.required && entry?.source !== "BULK_UPLOAD"}>
              <input type="file" multiple id="de-file" data-testid="de-file-input" className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
              <Button variant="outline" size="sm" onClick={() => document.getElementById("de-file").click()}><Paperclip className="mr-1 h-4 w-4" /> Upload (max {render.attachments.max_count})</Button>
              <span className="ml-2 text-xs text-slate-400">Any file size — auto-compressed below 500 KB. JPG, PNG, WEBP, PDF only.</span>
              <div className="mt-2 space-y-1">
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded border px-3 py-1.5 text-sm" data-testid={`de-att-${a.id}`}>
                    <span className="truncate">{a.original_filename}{" "}
                      {a.status === "attached" && <span className="text-xs text-slate-400">({Math.round(a.final_size / 1024)} KB)</span>}
                      {(a.status === "uploading" || a.status === "processing") && (
                        <span className="ml-1 inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-700" data-testid={`de-att-status-${a.id}`}>
                          <Loader2 className="h-3 w-3 animate-spin" />{a.status === "uploading" ? "Uploading…" : "Compressing…"}
                        </span>
                      )}
                      {a.status === "failed" && <span className="ml-1 rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700" data-testid={`de-att-failed-${a.id}`}>Failed: {a.error}</span>}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      {a.status === "attached" && <Button variant="ghost" size="sm" title="View attachment" onClick={() => viewAttachment(a)} data-testid={`de-att-view-${a.id}`}><Eye className="h-4 w-4 text-blue-600" /></Button>}
                      {a.status !== "uploading" && <Button variant="ghost" size="sm" onClick={() => deleteAttachment(a.id)} data-testid={`de-att-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Actions */}
          <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t bg-white py-3">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy || attachments.some((a) => a.status === "uploading")} title={attachments.some((a) => a.status === "uploading") ? "Wait for the upload to finish (compression can continue after submit)" : undefined} data-testid="de-submit">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Submit
            </Button>
            <span className="text-xs text-slate-500">Completes automatically when all materials are Acknowledged/Installed — otherwise it's saved as a draft.</span>
          </div>
        </div>
      )}

      {/* Bulk Upload dialog */}
      <Dialog open={!!bulk} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent className="max-w-2xl" data-testid="de-bulk-dialog">
          <DialogHeader><DialogTitle>Bulk Upload Data Entries</DialogTitle></DialogHeader>
          {bulk && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Form *</Label>
                  <Select value={bulk.form?.id || ""} onValueChange={(v) => setBulk((b) => ({ ...b, form: forms.find((f) => f.id === v), form_type: "" }))}>
                    <SelectTrigger className="mt-1" data-testid="de-bulk-form"><SelectValue placeholder="Select form" /></SelectTrigger>
                    <SelectContent>{forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Form Type *</Label>
                  <Select value={bulk.form_type || ""} onValueChange={(v) => { setBulk((b) => ({ ...b, form_type: v })); setBulkPreview(null); }} disabled={!bulk.form}>
                    <SelectTrigger className="mt-1" data-testid="de-bulk-type"><SelectValue placeholder="Select form type" /></SelectTrigger>
                    <SelectContent>{(bulk.form?.form_types || []).map((t) => <SelectItem key={t} value={t} data-testid={`de-bulk-type-${t}`}>{formTypeLabel(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {bulk.form && bulk.form_type && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" data-testid="de-bulk-template" onClick={downloadBulkTemplate}><Copy className="mr-1 h-4 w-4" />Download Template</Button>
                  <input ref={bulkFileRef} type="file" accept=".xlsx" className="hidden" onChange={uploadBulk} data-testid="de-bulk-file" />
                  <Button size="sm" data-testid="de-bulk-upload" onClick={() => bulkFileRef.current?.click()} disabled={bulkBusy} className="bg-blue-600 hover:bg-blue-700">
                    {bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Paperclip className="mr-1 h-4 w-4" />}Upload & Preview
                  </Button>
                </div>
              )}
              {bulkPreview && (
                <div className="space-y-2" data-testid="de-bulk-preview">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className="bg-slate-700">Rows: {bulkPreview.summary.total}</Badge>
                    <Badge className="bg-emerald-600">Will Complete: {bulkPreview.summary.will_complete}</Badge>
                    <Badge className="bg-amber-500">Will Draft: {bulkPreview.summary.will_draft}</Badge>
                    <Badge className="bg-red-600">Rejected: {bulkPreview.summary.rejected}</Badge>
                    {bulkPreview.summary.rejected > 0 && (
                      <Button size="sm" variant="outline" className="h-7 border-red-300 text-xs text-red-700 hover:bg-red-50" data-testid="de-bulk-dl-rejected" onClick={downloadRejected}>Download Rejected Rows (Excel)</Button>
                    )}
                  </div>
                  {bulkPreview.summary.rejected > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-red-200 bg-red-50 p-2 text-xs">
                      {bulkPreview.rows.filter((r) => r.category === "REJECTED").map((r) => (
                        <div key={r.row} data-testid={`de-bulk-reject-${r.row}`} className="text-red-700">Row {r.row}: {r.message}</div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Valid rows will be created in the register — those passing all completion checks as Completed, the rest as editable Drafts.</p>
                  {bulkResult && (bulkResult.completed + bulkResult.drafted) > 0 && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs" data-testid="de-bulk-saved-summary">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <span className="font-semibold text-emerald-800">
                        Saved to Data Entry Register: {bulkResult.completed} Completed, {bulkResult.drafted} Draft
                        {bulkResult.skipped > 0 ? ` · ${bulkResult.skipped} already existed (skipped)` : ""}
                        {bulkResult.failed > 0 ? ` · ${bulkResult.failed} failed` : ""}
                      </span>
                      <Button size="sm" variant="outline" className="ml-auto h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="de-bulk-open-register"
                        onClick={() => { closeBulk(); openTab("data-entry-register"); }}>
                        Open Data Entry Register
                      </Button>
                    </div>
                  )}
                  {bulkResult && (bulkResult.completed + bulkResult.drafted) === 0 && (
                    <div className="rounded-md border border-slate-300 bg-slate-50 p-2 text-xs font-semibold text-slate-600" data-testid="de-bulk-nothing-saved">
                      Nothing was saved from this file — see the reasons below.
                    </div>
                  )}
                  {bulkErrors && bulkErrors.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-red-200 bg-red-50 p-2 text-xs" data-testid="de-bulk-apply-errors">
                      <div className="mb-1 font-semibold text-red-700">Why rows failed:</div>
                      {bulkErrors.map((e, i) => <div key={i} className="text-red-700">Row {e.row ?? "?"}: {e.reason}</div>)}
                      {bulkResult?.failed > 0 && <div className="mt-1 text-slate-500 italic">Retry re-runs only the failed rows — saved rows are never duplicated.</div>}
                    </div>
                  )}
                  {bulkDrafts && bulkDrafts.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-sky-200 bg-sky-50 p-2 text-xs" data-testid="de-bulk-drafted">
                      <div className="mb-1 font-semibold text-sky-700">Saved as Drafts — NOT failed. These entries are in the Data Entry Register; they complete automatically via "Complete All Ready" once the materials reach Acknowledged/Installed:</div>
                      {bulkDrafts.map((e, i) => <div key={i} className="text-sky-700">Row {e.row ?? "?"}: {e.reason}</div>)}
                    </div>
                  )}
                  {bulkSkipped && bulkSkipped.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-2 text-xs" data-testid="de-bulk-skipped">
                      <div className="mb-1 font-semibold text-amber-700">Skipped — these materials are already saved in the register (not duplicated):</div>
                      {bulkSkipped.map((e, i) => <div key={i} className="text-amber-700">Row {e.row ?? "?"}: {e.reason}</div>)}
                    </div>
                  )}
                  {(bulkResult?.failed > 0 || bulkResult?.skipped > 0) && (
                    <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="de-bulk-force-wrap">
                      <input type="checkbox" className="mt-0.5" checked={bulkForce} onChange={(e) => setBulkForce(e.target.checked)} data-testid="de-bulk-force-replace" />
                      <span>These rows are blocked by drafts saved from an earlier upload of this form. Tick this to <b>delete those blocking drafts and re-create the rows from this file</b>. Completed entries are never touched; every deletion is audited.</span>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeBulk}>Close</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="de-bulk-apply" onClick={applyBulk}
              disabled={bulkBusy || !bulkPreview || (!(bulkResult?.failed > 0 || bulkResult?.skipped > 0) && (bulkPreview.summary.will_complete + bulkPreview.summary.will_draft) === 0)}>
              {bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
              {bulkBusy ? (bulkProg ? `Creating… ${bulkProg.done ?? 0}/${bulkProg.total ?? ""}` : "Creating…") : bulkResult?.failed > 0 ? `Retry ${bulkResult.failed} Failed Rows` : bulkResult?.skipped > 0 ? `Retry ${bulkResult.skipped} Skipped Rows` : `Create ${bulkPreview ? bulkPreview.summary.will_complete + bulkPreview.summary.will_draft : ""} Entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, required, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">{title} {required && <span className="text-red-500">*</span>}</h3>
      {children}
    </div>
  );
}

function CustomField({ cf, value, onChange }) {
  const label = <Label className="text-[11px]">{cf.label} {cf.required && <span className="text-red-500">*</span>}</Label>;
  const tid = `de-cf-${cf.field_id}`;
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");
  if (cf.type === "dropdown") {
    const activeOpts = (cf.options || []).filter((o) => o.active !== false).map((o) => ({ value: o.option_id, label: o.label }));
    if (cf.allow_custom && customMode) return (
      <div>{label}<div className="mt-1 flex items-center gap-2">
        <Input data-testid={`${tid}-custom`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type a value (one-time)" />
        <button type="button" className="whitespace-nowrap text-xs text-blue-600" onClick={() => { setCustomMode(false); onChange(""); }} data-testid={`${tid}-pick`}>Pick from list</button>
      </div></div>
    );
    return (
      <div>{label}<div className="mt-1"><SearchableSelect testid={tid} value={value || ""} onChange={onChange} options={activeOpts} placeholder="Select" />
        {cf.allow_custom && <button type="button" className="mt-1 text-xs text-blue-600" onClick={() => { setCustomMode(true); onChange(""); }} data-testid={`${tid}-custom-toggle`}>+ Enter custom value</button>}
      </div></div>
    );
  }
  if (cf.type === "multiselect") {
    const activeOpts = (cf.options || []).filter((o) => o.active !== false).map((o) => ({ value: o.option_id, label: o.label }));
    const arr = Array.isArray(value) ? value : [];
    const addCustom = () => { const t = customText.trim(); if (t && !arr.includes(t)) onChange([...arr, t]); setCustomText(""); };
    return (
      <div>{label}<div className="mt-1"><SearchableSelect testid={tid} multiple value={arr} onChange={onChange} options={activeOpts} placeholder="Select options" />
        {cf.allow_custom && <div className="mt-1 flex items-center gap-2">
          <Input className="h-7 text-xs" data-testid={`${tid}-custom`} value={customText} onChange={(e) => setCustomText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} placeholder="Add custom value…" />
          <button type="button" className="whitespace-nowrap text-xs text-blue-600" onClick={addCustom} data-testid={`${tid}-custom-add`}>Add</button>
        </div>}
      </div></div>
    );
  }
  if (cf.type === "checkbox" || cf.type === "boolean") return (
    <div className="flex items-center gap-2 pt-5"><Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} data-testid={tid} />{label}</div>
  );
  const inputType = cf.type === "number" ? "number" : cf.type === "date" ? "date" : "text";
  return <div>{label}<Input type={inputType} data-testid={tid} className="mt-1 h-9" value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}
